/*
 * Copyright 2021 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useApi } from '@backstage/core-plugin-api';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import { BackstageTheme } from '@backstage/theme';

import {
  Box,
  Button,
  Dialog,
  Grid,
  makeStyles,
  Typography,
} from '@material-ui/core';
import FavoriteIcon from '@material-ui/icons/Favorite';
import FavoriteBorderIcon from '@material-ui/icons/FavoriteBorder';

import { tomtomLabApiRef } from '../../api';
import { useEventTheme } from '../../context/EventThemeContext';
import { useIdeaEngagement } from '../../context/IdeaEngagementContext';
import { Idea } from '../../types';
import { Event, ListEventFilter } from '../../types';
import { useCanJuryScore } from '../../util/juryAssessmentMethods';
import { isSharedIdeaLink, redirectToIdeasUrl } from '../../util/parseMethods';
import { IdeaDetailsDialog } from '../HomePageBazaarInfoCard';
import { StatusTag } from '../StatusTag/StatusTag';
import buttonImage from '../images/2026-images-main-landing-page/button.png';

type Props = {
  idea: Idea;
  fetchIdeas: () => Promise<Idea[]>;
  height: 'large' | 'small';
};

const useStyles = makeStyles<BackstageTheme, { cosmic: boolean }>(theme => ({
  statusTag: {
    display: 'inline-block',
    whiteSpace: 'nowrap',
    marginBottom: '0.8rem',
  },
  row: {
    borderBottomStyle: 'solid',
    borderBottomColor: ({ cosmic }) =>
      cosmic ? 'rgba(0,0,0,0.1)' : theme.palette.divider,
    cursor: 'pointer',
    backgroundColor: 'transparent',
    width: '100%',
    margin: '0 auto',
  },
  item: {
    padding: '10px 0px 0px 10px !important',
  },
  summary: {
    overflow: 'auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
  },
  headerRight: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  likes: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.1rem',
  },
  likeIndicator: {
    display: 'inline-flex',
    alignItems: 'center',
    color: theme.palette.error.main,
    padding: '4px',
  },
  likesIcon: {
    fontSize: '1.1rem',
    display: 'block',
  },
  button: { minWidth: '11rem' },
  container: {
    marginTop: '2rem',
  },
  pullRight: {
    marginLeft: 'auto',
  },
  scoreButton: {
    marginTop: '12px',
  },
  scoreButtonCosmic: {
    marginTop: '12px',
    backgroundImage: `url(${buttonImage})`,
    backgroundSize: '100% 100%',
    backgroundRepeat: 'no-repeat',
    color: '#000000',
    fontFamily: 'Carbon, sans-serif',
    fontSize: '1rem',
    fontWeight: 100,
    letterSpacing: '0.1em',
    textTransform: 'none',
    padding: '0 !important',
    height: 38,
    width: 110,
    minWidth: 'unset',
    '&:hover': {
      backgroundImage: `url(${buttonImage})`,
      backgroundSize: '100% 100%',
    },
  },
}));

export const IdeaRowCard = ({ idea, fetchIdeas }: Props) => {
  const { isCosmicTheme: cosmic } = useEventTheme();
  const classes = useStyles({ cosmic });
  const {
    id,
    title,
    status,
    updatedAt,
    summary,
    membersCount,
    eventId,
    responsible,
  } = idea;
  const { liked, likeCount, seed } = useIdeaEngagement(id);
  const [eventTitle, setEventTitle] = useState('');

  // The ideas query already carries the counts, so seed from the row rather than
  // firing a request per card.
  useEffect(() => {
    seed({ likes: idea.likes, likedByMe: idea.likedByMe });
  }, [seed, idea.likes, idea.likedByMe]);

  const tomtomLabApi = useApi(tomtomLabApiRef);
  const canJuryScore = useCanJuryScore(idea);

  useEffect(() => {
    const eventFilter: ListEventFilter = {
      order: 'latest',
    };

    const fetchEventTitle = async () => {
      const event = await tomtomLabApi.getEvents(eventFilter);
      const currentEventTitle = event.find(
        (e: Event) => e.id === eventId,
      )?.title;
      if (!currentEventTitle) return 'No event';
      return currentEventTitle;
    };

    const fetchTitle = async () => {
      setEventTitle(await fetchEventTitle());
    };
    fetchTitle();
  }, [eventId, tomtomLabApi]);

  const [openCard, setOpenCard] = useState(false);

  const handleClose = () => {
    setOpenCard(false);
    fetchIdeas();
    if (isSharedIdeaLink()) {
      redirectToIdeasUrl();
    }
  };

  return (
    <>
      <Dialog fullWidth maxWidth="lg" onClose={handleClose} open={openCard}>
        <IdeaDetailsDialog initIdea={idea} handleClose={handleClose} />
      </Dialog>
      <Grid
        container
        spacing={3}
        onClick={() => setOpenCard(true)}
        key={id}
        className={classes.row}
        component="tr"
      >
        <Grid item md={12} component="td" className={classes.item}>
          <Box className={classes.header}>
            <Typography variant="h5">{title}</Typography>
            <Box className={classes.headerRight}>
              <Box
                className={classes.likes}
                aria-label={`${likeCount} ${
                  likeCount === 1 ? 'like' : 'likes'
                }${liked ? ', including yours' : ''}`}
              >
                <Box
                  component="span"
                  className={classes.likeIndicator}
                  aria-hidden="true"
                >
                  {liked ? (
                    <FavoriteIcon className={classes.likesIcon} />
                  ) : (
                    <FavoriteBorderIcon className={classes.likesIcon} />
                  )}
                </Box>
                <Typography variant="body1">{likeCount}</Typography>
              </Box>
              {canJuryScore &&
                (cosmic ? (
                  <Button
                    className={classes.scoreButtonCosmic}
                    component={Link}
                    to={`/tomtom-lab/jury-assessment/${id}`}
                  >
                    SCORE
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    className={classes.scoreButton}
                    color="primary"
                    component={Link}
                    to={`/tomtom-lab/jury-assessment/${id}`}
                  >
                    SCORE
                  </Button>
                ))}
            </Box>
          </Box>
        </Grid>
        <Grid item md={12} component="td" className={classes.item}>
          <Typography variant="body1" className={classes.summary}>
            {summary}
          </Typography>
        </Grid>
        <Grid item md={2} component="td" className={classes.item}>
          <Typography variant="body2">
            {
              // eslint-disable-next-line
              Intl.DateTimeFormat('en-us', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              }).format(new Date(updatedAt || ''))
            }
          </Typography>
        </Grid>
        <Grid item md={2} component="td" className={classes.item}>
          <StatusTag styles={classes.statusTag} status={status} />
        </Grid>
        <Grid item md={4} component="td" className={classes.item}>
          <Typography variant="body1">
            Responsible: (
            <EntityRefLink
              // make use ref back into a TomTom email
              title={responsible.split('/').pop()?.replace('_', '@') ?? ''}
              entityRef={responsible}
              defaultKind="User"
            />
            )
          </Typography>
        </Grid>
        <Grid item md={1} component="td" className={classes.item}>
          <Typography variant="body1">{`Members: ${membersCount}`}</Typography>
        </Grid>
        <Grid item md={3} component="td" className={classes.item}>
          <Box display="flex" justifyContent="flex-end" alignItems="flex-end">
            <Typography variant="body1">{`${eventTitle}`}</Typography>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};
